import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface EditTitleDialogProps {
  conversationId: string | null;
  currentTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditTitleDialog({
  conversationId,
  currentTitle,
  isOpen,
  onClose,
}: EditTitleDialogProps) {
  const { t } = useTranslation();
  const [titleValue, setTitleValue] = useState(currentTitle);

  // Update title when dialog opens with new conversation
  useEffect(() => {
    if (isOpen) {
      setTitleValue(currentTitle);
    }
  }, [isOpen, currentTitle]);

  // Mutation for updating conversation title
  const updateTitleMutation = useMutation({
    mutationFn: (data: { conversationId: string; title: string }) =>
      apiRequest("PATCH", `/api/conversations/${data.conversationId}/title`, {
        title: data.title,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/conversations"] });
      onClose();
    },
  });

  const handleSave = () => {
    if (conversationId && titleValue.trim()) {
      updateTitleMutation.mutate({
        conversationId,
        title: titleValue.trim(),
      });
    }
  };

  const handleClose = () => {
    setTitleValue(currentTitle); // Reset to original value
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0a0c10] border-white/[0.1]">
        <DialogHeader>
          <DialogTitle className="text-white">{t("editTitle.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            placeholder={t("editTitle.placeholder")}
            className="bg-white/[0.08] border-white/[0.1] text-white"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} className="text-white/60 hover:text-white">
            {t("editTitle.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={updateTitleMutation.isPending}
          >
            {updateTitleMutation.isPending ? t("editTitle.saving") : t("editTitle.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
